import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "tenant_user" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isAdmin = user?.role === "tenant_admin";

  const load = async () => {
    if (!isAdmin) return;
    const { data } = await api.get("/tenant/users");
    setItems(data);
  };
  useEffect(() => { load(); }, [isAdmin]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/tenant/users", form);
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "tenant_user" });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this user?")) return;
    await api.delete(`/tenant/users/${id}`);
    load();
  };

  if (!isAdmin) {
    return <Card className="p-8 border-slate-100"><p className="text-slate-700">Only tenant admins can manage team members.</p></Card>;
  }

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between">
        <p className="text-slate-600">Invite teammates and manage roles.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="team-invite-btn" className="bg-[#F58220] hover:bg-[#E36F0E] text-white"><Plus size={16} className="mr-1.5"/>Invite user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a teammate</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label className="text-sm font-semibold text-[#1F3A6B]">Name</Label><Input data-testid="team-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="h-11 mt-1"/></div>
              <div><Label className="text-sm font-semibold text-[#1F3A6B]">Email</Label><Input data-testid="team-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required className="h-11 mt-1"/></div>
              <div><Label className="text-sm font-semibold text-[#1F3A6B]">Initial password</Label><Input data-testid="team-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required minLength={8} className="h-11 mt-1"/></div>
              <div>
                <Label className="text-sm font-semibold text-[#1F3A6B]">Role</Label>
                <select data-testid="team-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="h-11 mt-1 w-full border border-slate-200 rounded-md px-3 bg-white">
                  <option value="tenant_user">Member</option>
                  <option value="tenant_admin">Admin</option>
                </select>
              </div>
              {err && <p className="text-sm text-rose-600">{err}</p>}
              <DialogFooter><Button data-testid="team-submit" disabled={busy} type="submit" className="bg-[#F58220] hover:bg-[#E36F0E] text-white">{busy ? "Inviting…" : "Invite"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-semibold text-[#1F3A6B]">{u.name}</td>
                <td className="px-4 py-3 text-slate-700">{u.email}</td>
                <td className="px-4 py-3"><Badge className={u.role === "tenant_admin" ? "bg-cyan-100 text-[#1F3A6B]" : "bg-slate-100 text-slate-700"}>{u.role.replace("_"," ")}</Badge></td>
                <td className="px-4 py-3">{u.is_active ? <span className="text-emerald-600 text-xs font-bold">Active</span> : <span className="text-slate-500 text-xs">Disabled</span>}</td>
                <td className="px-4 py-3 text-right">
                  {u.id !== user.id && <Button data-testid={`team-del-${u.id}`} variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700" onClick={() => remove(u.id)}><Trash2 size={14}/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
